/* =============================================================================
 * Delta Clothing — per-vendor free-shipping progress bars
 *
 * Two thresholds:
 *   - TeeLand subtotal >= $250 → free shipping on Teeland line items
 *   - S&S Activewear subtotal >= $150 → free shipping on S&S line items
 *
 * Without separate bars, a customer who hits $300 total via $250 Teeland +
 * $50 S&S thinks the whole order ships free, but S&S still bills Delta for
 * the $50 shipment. Splitting the bars by vendor keeps the customer honest
 * about which threshold each side crossed.
 *
 * The bars render into any `[data-shipping-bars]` mount on the cart page or
 * cart drawer — both ship a placeholder div from the b2b-cart-page /
 * b2b-cart-drawer snippets. JS reads the current __dlc_bag cache (kept fresh
 * by b2b-cart-store) and recomputes on `dlc:cart-changed`.
 *
 * Source-of-truth thresholds + vendor-detection live here so we can tune
 * them without redeploying every other bundle.
 *
 * Depends on window.B2B + window.__dlc_bag.
 * =============================================================================*/
(function () {
  if (!window.B2B || window.__dlc_shipping_bars_installed) return;
  window.__dlc_shipping_bars_installed = true;

  var THRESHOLDS = {
    teeland: 250,
    ss: 150
  };
  var VENDOR_LABELS = {
    teeland: 'TeeLand',
    ss: 'S&S Activewear'
  };

  // Vendor key resolver — line.custom.Vendor / line.product[0].vendor /
  // line.sku prefix all hint at the source. Default: bucket anything we
  // can't classify with confidence under "ss" so the threshold is the
  // stricter one (don't undersell free shipping).
  function bucket(line) {
    var custom = (line && line.custom) || {};
    var vendor = String(custom.Vendor || (line.product && line.product[0] && line.product[0].vendor) || '').toLowerCase();
    var sku = String(line.sku || custom.SKU || '');

    if (/teeland|delta|^1001|^te-?\d/.test(vendor + ' ' + sku)) return 'teeland';
    return 'ss';
  }

  function totalsFromBag(lines) {
    var totals = { teeland: 0, ss: 0 };
    (lines || []).forEach(function (l) {
      var qty = parseInt(l.quantity, 10) || 0;
      var unit = parseFloat(l.price) || 0;
      var key = bucket(l);
      totals[key] += qty * unit;
    });
    return totals;
  }

  function renderBars(mount, lines) {
    var totals = totalsFromBag(lines);
    // Brand review #3 (#1): drawer mounts pass data-compact="1" so the
    // painter emits a 2-line layout (heading on top, bar + numbers on a
    // single inline row) instead of the 3-row card. The $X-to-free hint
    // is dropped in compact mode to keep the rail narrow.
    var compact = mount.hasAttribute('data-compact');
    var html = '';
    Object.keys(THRESHOLDS).forEach(function (key) {
      var threshold = THRESHOLDS[key];
      var current = Math.round(totals[key] * 100) / 100;
      var pct = Math.max(0, Math.min(100, threshold === 0 ? 100 : (current / threshold) * 100));
      var qualified = current >= threshold;
      var remaining = Math.max(0, threshold - current);
      var label = VENDOR_LABELS[key];

      html += '<div class="b2b-ship-bar' + (qualified ? ' is-qualified' : '') + (current === 0 ? ' is-empty' : '') + '" data-vendor="' + key + '">';
      html += '<div class="b2b-ship-bar__head">';
      html += '<span class="b2b-ship-bar__label">' + label + '</span>';
      if (current === 0) {
        html += '<span class="b2b-ship-bar__hint">Free shipping over $' + threshold.toFixed(0) + '</span>';
      } else if (qualified) {
        html += '<span class="b2b-ship-bar__hint b2b-ship-bar__hint--ok">✓ Free shipping unlocked</span>';
      } else {
        html += '<span class="b2b-ship-bar__hint">$' + remaining.toFixed(2) + ' to free shipping</span>';
      }
      html += '</div>';
      if (compact) {
        html += '<div class="b2b-ship-bar__row">';
        html += '<div class="b2b-ship-bar__track"><div class="b2b-ship-bar__fill" style="width:' + pct.toFixed(1) + '%"></div></div>';
        html += '<div class="b2b-ship-bar__numbers">$' + current.toFixed(2) + ' / $' + threshold.toFixed(0) + '</div>';
        html += '</div>';
      } else {
        html += '<div class="b2b-ship-bar__track"><div class="b2b-ship-bar__fill" style="width:' + pct.toFixed(1) + '%"></div></div>';
        html += '<div class="b2b-ship-bar__numbers">$' + current.toFixed(2) + ' / $' + threshold.toFixed(0) + '</div>';
      }
      html += '</div>';
    });
    mount.innerHTML = html;

    // Surface an aggregate "any vendor under threshold" flag on the mount
    // so the cart page can show its "Estimated shipping fee — calculated
    // at checkout" copy when needed. The actual fee line lives on the
    // Shopify DraftOrder; this is just a UI hint.
    var anyShort = totals.teeland > 0 && totals.teeland < THRESHOLDS.teeland;
    anyShort = anyShort || (totals.ss > 0 && totals.ss < THRESHOLDS.ss);
    mount.setAttribute('data-shipping-shortfall', anyShort ? '1' : '0');
  }

  function paintAll() {
    var mounts = document.querySelectorAll('[data-shipping-bars]');
    if (!mounts.length) return;
    var lines = (window.__dlc_bag && window.__dlc_bag.getCache && window.__dlc_bag.getCache()) || [];
    mounts.forEach(function (m) { renderBars(m, lines); });
  }

  function init() {
    paintAll();
    // Lazy-refresh once the cache has loaded for the first time.
    if (window.__dlc_bag && typeof window.__dlc_bag.load === 'function') {
      window.__dlc_bag.load(false).then(paintAll).catch(function () {});
    }
  }

  window.addEventListener('dlc:cart-changed', paintAll);
  // Cart page or drawer may be mounted dynamically — observe insertion.
  var observer = new MutationObserver(function () {
    if (document.querySelector('[data-shipping-bars]:not([data-shipping-bars-painted])')) {
      paintAll();
      document.querySelectorAll('[data-shipping-bars]').forEach(function (m) {
        m.setAttribute('data-shipping-bars-painted', '1');
      });
    }
  });
  try { observer.observe(document.body, { childList: true, subtree: true }); } catch (e) {}

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    setTimeout(init, 0);
  }

  // Public surface for ops tweaking thresholds at runtime / debugging.
  window.B2BShipping = {
    thresholds: THRESHOLDS,
    repaint: paintAll
  };
})();
